// app.config.ts
import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, importProvidersFrom } from '@angular/core';
import { provideRouter, Router } from '@angular/router';
import { routes } from './app.routes';
import { environment } from 'src/environments/environment';
import * as Sentry from '@sentry/angular';

import { APOLLO_OPTIONS, Apollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { ApolloClientOptions, InMemoryCache, split } from '@apollo/client/core';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { tokenInterceptor } from './core/interceptors/token.interceptor';
import { MarkdownModule, provideMarkdown } from 'ngx-markdown';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { QuillModule } from 'ngx-quill';
import hljs from 'highlight.js/lib/core';
import sql from 'highlight.js/lib/languages/sql';
import 'highlight.js/styles/github.css';
import { createClient } from 'graphql-ws';
import { getMainDefinition } from '@apollo/client/utilities';

// your own Firebase singletons
import { firebaseAuth, initializeFirebaseAuthPersistence } from './core/firebase';

hljs.registerLanguage('sql', sql);

// optional: only run persistence in browser
function initAuth() {
  return () => {
    if (typeof window !== 'undefined') {
      return initializeFirebaseAuthPersistence();
    }
    return Promise.resolve();
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),

    { provide: ErrorHandler, useValue: Sentry.createErrorHandler() },
    { provide: Sentry.TraceService, deps: [Router] },
    { provide: APP_INITIALIZER, useFactory: () => () => { }, deps: [Sentry.TraceService], multi: true },

    // initialize Firebase Auth persistence once
    { provide: APP_INITIALIZER, useFactory: initAuth, multi: true },

    importProvidersFrom([
      // AngularFire providers removed
      MarkdownModule.forRoot(),
      QuillModule.forRoot(),
    ]),

    Apollo,
    {
      provide: APOLLO_OPTIONS,
      useFactory: (httpLink: HttpLink): ApolloClientOptions<any> => {
        const http = httpLink.create({ uri: environment.graphql });

        const ws = new GraphQLWsLink(
          createClient({
            url: environment.graphqlWs,
            connectionParams: {
              Authorization: localStorage.getItem('tif-access-token'),
            },
          })
        );

        const link = split(
          ({ query }) => {
            const definition = getMainDefinition(query);
            return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
          },
          ws,
          http
        );

        return { cache: new InMemoryCache(), link };
      },
      deps: [HttpLink],
    },

    provideHttpClient(withInterceptors([tokenInterceptor])),
    provideAnimationsAsync(),
    provideMarkdown(),
  ],
};

import {
  EnterpriseCoreModule,
  ClipboardModule,
  ColumnsToolPanelModule,
  ExcelExportModule,
  FiltersToolPanelModule,
  GridChartsModule,
  MasterDetailModule,
  MenuModule,
  MultiFilterModule,
  RangeSelectionModule,
  RichSelectModule,
  RowGroupingModule,
  ServerSideRowModelModule,
  SetFilterModule,
  SideBarModule,
  SparklinesModule,
  StatusBarModule,
  ViewportRowModelModule,
  ClientSideRowModelModule
} from 'ag-grid-enterprise';
import { ModuleRegistry } from 'ag-grid-community';

ModuleRegistry.registerModules([
  EnterpriseCoreModule,
  ClipboardModule,
  ColumnsToolPanelModule,
  ExcelExportModule,
  FiltersToolPanelModule,
  GridChartsModule,
  MasterDetailModule,
  MenuModule,
  MultiFilterModule,
  RangeSelectionModule,
  RichSelectModule,
  RowGroupingModule,
  ServerSideRowModelModule,
  SetFilterModule,
  SideBarModule,
  SparklinesModule,
  StatusBarModule,
  ViewportRowModelModule,
  ClientSideRowModelModule
]);
